<script>
import {
    defineComponent,
    computed,
    h,
    TransitionGroup,
    cloneVNode,
    isVNode,
} from 'vue';

export default defineComponent({
    name: 'MvtReorder',
    props: {
        order: {
            type: Array,
            default: () => [],
        },
        /**
         * When set, children are wrapped in Vue's TransitionGroup with this `name`
         * (e.g. "card" → classes like .card-enter-active, .card-move).
         * Each slotted vnode should set `orderKey` so list keys stay stable for enter/move/leave.
         */
        transitionName: {
            type: String,
            default: 'mvt-reorder',
        },
        /** Root element tag when using transitionName (TransitionGroup `tag`). */
        tag: {
            type: String,
            default: 'div',
        },
    },

    setup(props, { slots, attrs }) {
        const orderedChildren = computed(() => {
            const vnodes = slots.default ? slots.default() : [];

            if (!props.order || props.order.length === 0) {
                return vnodes;
            }

            const byKey = new Map();
            const unordered = [];

            for (const vnode of vnodes) {
                const key = vnode.props && vnode.props.orderKey;

                if (key && props.order.includes(key)) {
                    if (!byKey.has(key)) {
                        byKey.set(key, []);
                    }
                    byKey.get(key).push(vnode);
                } else {
                    unordered.push(vnode);
                }
            }

            const result = [];

            // push in configured order
            for (const key of props.order) {
                const bucket = byKey.get(key);
                if (bucket && bucket.length > 0) {
                    for (let i = 0; i < bucket.length; i++) {
                        result.push(bucket[i]);
                    }
                }
            }

            // then everything else
            for (let i = 0; i < unordered.length; i++) {
                result.push(unordered[i]);
            }

            return result;
        });

        const childrenForRender = computed(() => {
            const list = orderedChildren.value;
            if (!props.transitionName) {
                return list;
            }
            return list.map((vnode, i) => {
                if (!isVNode(vnode)) {
                    return vnode;
                }
                const k =
                    vnode.props?.orderKey ?? vnode.key ?? `__mvt-${i}`;
                return cloneVNode(vnode, { key: String(k) });
            });
        });

        return () => {
            const children = childrenForRender.value;

            if (props.transitionName) {
                return h(
                    TransitionGroup,
                    {
                        name: props.transitionName,
                        tag: props.tag,
                        ...attrs,
                    },
                    () => children
                );
            }

            return h(props.tag, attrs, children);
        };
    },
});
</script>

<style lang="scss">
@use "./mvt-reorder.scss" as *;
</style>